import { setSession } from "../components/account/auth-helper";
import { showToast, updateToastProgress } from "../components/Toasts";
import Logger from "../Logger";
import ProjectMetadata, { UserReference } from "../models/ProjectMetadata";
import { State } from "../State";
import { itx } from "../translationItx/itx";
import BaseStorageHandler from "./BaseStorageHandler";
import getLocalStorageHandler from "./LocalstorageHandler";
import { idsToDeleteAfterManualSnapshot } from "./SnapshotRetention";
import ServerDataBaseHandler from "./SeverDataBaseHandler";
import ZipStorageHandler from "./ZipStorageHandler";

const logger = new Logger();

export enum enumStorageMode {
  local = "local", // no itx
  server = "server", // no itx
}
export class UniversalstorageHandler {
  ////////////////////////////////////////////////////////////

  private _storageHandler: BaseStorageHandler | undefined;
  public get storageHandler(): BaseStorageHandler {
    if (!this._storageHandler) {
      this._storageHandler = new ServerDataBaseHandler();
    }
    return this._storageHandler;
  }

  public set storageHandler(value: BaseStorageHandler) {
    this._storageHandler = value;
  }

  ////////////////////////////////////////////////////////////

  private _localStorageHandler: ReturnType<typeof getLocalStorageHandler> | undefined;
  public get localStorageHandler() {
    if (!this._localStorageHandler) {
      this._localStorageHandler = getLocalStorageHandler();
    }
    return this._localStorageHandler;
  }

  ////////////////////////////////////////////////////////////

  temporaryOffline = false;

  creditals: any | undefined;

  get storageMode(): enumStorageMode {
    var res = localStorage.getItem("storageMode") || enumStorageMode.server;

    if (res == enumStorageMode.server) {
      //
      // check if we are online
      //
      if (this.temporaryOffline) {
        return enumStorageMode.local;
      }
    }
    return res as enumStorageMode;
  }

  set storageMode(newValue) {
    localStorage.setItem("storageMode", newValue);
  }

  updateStorageHandler() {
    if (this.storageMode == enumStorageMode.server) {
      this.storageHandler = new ServerDataBaseHandler();
    } else {
      this.storageHandler = getLocalStorageHandler();
    }
  }

  constructor() {
    this.updateStorageHandler();
  }

  //public storageHandler = new ServerDataBaseHandler();

  async saveProjectMetaData(projectMetadata: ProjectMetadata): Promise<boolean> {
    if (!(await this.ensureRestoredProjectUsers(projectMetadata))) return false;
    const saved = await this.storageHandler.saveProjectMetadata(projectMetadata.id, projectMetadata.toJSON());
    if (!saved) return false;
    // Also update the local copy so that loadProjectLocal (beta/offline path)
    // reads the current metadata instead of the stale pre-rename version.
    if (!this.storageHandler.isLocalStorgeHandle) {
      const localMetadata = await this.localStorageHandler.loadProjectMetadata(projectMetadata.id);
      if (localMetadata) {
        await this.localStorageHandler.saveProjectMetadata(projectMetadata.id, projectMetadata.toJSON());
      }
    }
    return true;
  }

  private async loadCurrentProjectUsers(projectId: string): Promise<UserReference[] | undefined> {
    if (!navigator.onLine) return undefined;
    try {
      const data = await new ServerDataBaseHandler().loadProjectMetadata(projectId);
      if (!Array.isArray(data?.users)) return undefined;
      return data.users.map(user => UserReference.fromUser(user));
    } catch (error) {
      logger.error("Could not refresh restored project users", error);
      return undefined;
    }
  }

  private async ensureRestoredProjectUsers(metadata: ProjectMetadata): Promise<boolean> {
    if (this.storageHandler.isLocalStorgeHandle || !metadata.projectUsersRefreshRequired) return true;
    const users = await this.loadCurrentProjectUsers(metadata.id);
    if (users === undefined) {
      await showToast({ isError: true, text: itx("saveProject.projectUsersRefreshFailed",
        "Die aktuellen Projektmitarbeiter konnten nicht vom Server geladen werden. Bitte erneut speichern, sobald der Server erreichbar ist.") });
      return false;
    }
    metadata.users = users;
    metadata.projectUsersRefreshRequired = false;
    return true;
  }

  async saveLocalCopyOfProject2(blob: Blob, isSnapShot: boolean): Promise<boolean> {
    let postfix = "";
    let d = new Date();
    const projectId = state.projectMetadata.id;

    if (isSnapShot) {
      postfix = "-snapshot-" + d.toISOString();
    }

    const storedProjectId = projectId + postfix;
    const saveLocalOk = await this.localStorageHandler.saveProjectItems2(storedProjectId, blob);

    if (saveLocalOk) {
      state.projectMetadata.localCopied = new Date();
      state.projectMetadata.lastModifiedLocal = undefined;

      const metadataJson = state.projectMetadata.toJSON();
      if (isSnapShot) {
        metadataJson.mainId = metadataJson.id;
        metadataJson.description +=
          " " +
          itx("saveProject.snapshot", "Schnappschuss") +
          " " +
          d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "2-digit", // no itx
            day: "2-digit", // no itx
          }) +
          " " +
          d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); // no itx

        metadataJson.id = storedProjectId;
      }

      const metadataSaved = await this.localStorageHandler.saveProjectMetadata(storedProjectId, metadataJson);
      if (isSnapShot && metadataSaved) {
        await this.cleanupAfterManualSnapshot(projectId, d);
      }
      state.projectMetadata.raiseOnChange("localCopied");
    }

    return saveLocalOk;
  }

  private async cleanupAfterManualSnapshot(projectId: string, snapshotDate: Date): Promise<void> {
    const projects = await this.localStorageHandler.loadProjectList(state.loggedInUser.email);
    const idsToDelete = idsToDeleteAfterManualSnapshot(projects, projectId, snapshotDate);

    await Promise.all(
      idsToDelete.map(async (id) => {
        await this.localStorageHandler.deleteProjectItems(id);
        await this.localStorageHandler.deleteProjectMetadata(id);
      }),
    );
    state.clearAutobackupStatus();
  }

  private activeProjectSaves = 0;

  get isSavingProject(): boolean {
    return this.activeProjectSaves > 0;
  }

  async saveProject(showErrors?: boolean): Promise<boolean> {
    this.activeProjectSaves++;
    const metadata = state.projectMetadata;
    let saved = false;
    try {
      saved = await this.saveProjectInternal(showErrors);
      return saved;
    } finally {
      this.activeProjectSaves--;
      if (!saved) metadata.markAsDirty();
    }
  }

  private async saveProjectInternal(showErrors?: boolean): Promise<boolean> {
    if (!(await this.ensureRestoredProjectUsers(state.projectMetadata))) return false;
    const modified = state.isDirty;
    showErrors = showErrors === undefined ? true : showErrors;

    let modifiedDate: Date | undefined;
    //
    // mwe 7.10.2024
    // set modified date ALLWAYS if we save the project
    //
    if (modified || true) {
      modifiedDate = new Date();

      if (this.storageHandler.isLocalStorgeHandle) {
        state.projectMetadata.lastModifiedLocal = modifiedDate;
      } else {
        state.projectMetadata.lastModifiedOnServer = modifiedDate;
      }
    }

    const title = itx("saveProject.projectWillBeSaved", "Projekt wird gespeichert");

    await showToast({
      title: title,
      text: itx("saveProject.compress", "Komprimierung"),
      keepVisible: true,
      modal: true,
    });

    const z = new ZipStorageHandler();
    let ok = await z.saveProjectItems(state.projectMetadata.id, showErrors);
    ok &&= await z.saveDeletedIds();

    //
    // save a copy of the metadata inside the zip for worst case revovery
    //
    await z.saveProjectMetadata(state.projectMetadata.id, state.projectMetadata);

    const blob = await z.getBlob();

    if (!ok || !blob) {
      await showToast({
        isError: true,
        title: itx("UniversalstorageHandler.errorWhileCompressing", "Fehler beim der Komprimierung"),
        text: itx("UniversalstorageHandler.savingCanceled", "Speicherung abgebrochen"),
        keepVisible: true,
        modal: false,
      });

      return false;
    }

    let saveLocalCopyOk = true;

    if (!this.storageHandler.isLocalStorgeHandle) {
      //
      //
      // if we are saveing to the server,
      // save also a local copy
      //

      await showToast({
        title: title,
        text: itx("saveProject.saveLocalCopy", "Lokale Kopie speichern"),
        keepVisible: true,
        modal: true,
      });

      saveLocalCopyOk = await this.saveLocalCopyOfProject2(blob, false);

      //
      // save snapshot as Backup
      //

      await this.saveLocalCopyOfProject2(blob, true);
    }

    //////////////////////////////////////////////////////////////////////////////////////
    //
    // MWE 29.1.2026 überprüfen ob das überhaupt notwendig ist !!!!
    //
    // Im schlimmsten Fall würden wir hier das Projekt löschen,
    // und im nächsten Schritt unter Umständen nicht erfolgreich speichern
    //
    //

    await this.storageHandler.deleteProjectItems(state.projectMetadata.id);

    //////////////////////////////////////////////////////////////////////////////////////

    if (state) {
      state.projectMetadata.updateNewStatistic();
      // could be if license was added with other users?
      state.projectMetadata.updateProjectLicence();
    }

    await showToast({
      title: title,
      text: state.translate("saveProject.storeInCloud", "In der Cloud speichern"),
      keepVisible: true,
      modal: true,
      progress: true,
    });

    const ok2 = await this.storageHandler.saveProjectItems2(state.projectMetadata.id, blob);

    if (ok2) {
      const metadataSaved = await this.storageHandler.saveProjectMetadata(state.projectMetadata.id, state.projectMetadata.toJSON());
      updateToastProgress({ progress: 100 });
      return metadataSaved;
    } else {
      await showToast({
        title: itx("UniversalstorageHandler.dasspeichernin", "Das Speichern in der der Cloud ist fehlgeschlagen"),
        text: saveLocalCopyOk
          ? itx("UniversalstorageHandler.lokalekopiewurde", "Lokale Kopie wurde erfolgreich gespeichert")
          : itx(
              "UniversalstorageHandler.dasspeichernder",
              "Das Speichern der lokalen Kopie ist ebenfalls fehlgeschlagen",
            ),
        keepVisible: true,
        modal: true,
        progress: true,
      });
      return false;
    }
  }

  async loadCompareProject(projectId): Promise<boolean> {
    const saveState = globalThis.state;
    (window as any).state = (window as any)._state = globalThis.state = new State();
    globalThis.state.clear();

    let status: Blob | undefined;
    const loadLocal = false;
    if (loadLocal) {
      status = await universalstorageHandler.loadProjectLocal(projectId);
    } else {
      status = await universalstorageHandler.loadProject(projectId);
    }

    state.objectRegistry.allObjects.forEach((obj) => {
      obj._state = state;
    });

    globalThis.compareState = globalThis.state;
    (window as any).state = (window as any)._state = globalThis.state = saveState;

    return true;
  }

  async loadProjectLocal(projectId: string): Promise<Blob | undefined> {
    const previousSuppressUndoObservation = state.suppressUndoObservation;
    let blob: Blob | undefined;
    state.suppressUndoObservation = true;
    try {
      const data = await this.localStorageHandler.loadProjectMetadata(projectId);
      if (!data) return undefined;

      const snapshotPosition = projectId.indexOf("-snapshot");
      const originalProjectId =
        data.mainId || (snapshotPosition > 0 ? projectId.substring(0, snapshotPosition) : projectId);

      // New local-only projects have no server membership list yet and must still
      // be allowed to perform their first cloud save.
      const restoresExistingProject = snapshotPosition > 0 || !!data.mainId || !!data.localCopied || data.loadedFrom === "server";
      const currentUsers = await this.loadCurrentProjectUsers(originalProjectId);

      // State.loadFromEstadaStorage needs the project kind before restoring
      // project objects so that the Concept One project payload is included.
      state.projectMetadata.fromJSON({ ...data, id: originalProjectId,
        projectUsersRefreshRequired: currentUsers === undefined && (restoresExistingProject || data.projectUsersRefreshRequired === true),
        ...(currentUsers !== undefined ? { users: currentUsers } : {}) });
      blob = await this.localStorageHandler.loadProjectItems(projectId);
    } finally {
      if (blob) {
        if (state.undoManager.hasUndoEntries) {
          logger.error("Undo entries were created while loading the local project");
        }
        state.enableUndoMonitoring();
        state.undoManager.clear();
        state.suppressUndoObservation = false;
      } else {
        state.suppressUndoObservation = previousSuppressUndoObservation;
      }
    }
    return blob;
  }

  async loadProject(projectId: string): Promise<Blob | undefined> {
    if (this.storageHandler.isLocalStorgeHandle) return this.loadProjectLocal(projectId);
    const previousSuppressUndoObservation = state.suppressUndoObservation;
    let blob: Blob | undefined;
    state.suppressUndoObservation = true;
    try {
      const data = await this.storageHandler.loadProjectMetadata(projectId);
      if (!data) return undefined;
      data.loadedFrom = this.storageHandler.isLocalStorgeHandle ? "local" : "server"; // no itx
      // The item loader branches on isC1Project, therefore metadata must be
      // installed before the project object graph is restored.
      state.projectMetadata.fromJSON({ ...data, projectUsersRefreshRequired: !Array.isArray(data.users) });
      blob = await this.storageHandler.loadProjectItems(projectId);
      return blob;
    } finally {
      if (blob) {
        if (state.undoManager.hasUndoEntries) {
          logger.error("Undo entries were created while loading the project including its metadata");
        }
        state.enableUndoMonitoring();
        state.undoManager.clear();
        state.suppressUndoObservation = false;
      } else {
        state.suppressUndoObservation = previousSuppressUndoObservation;
      }
    }
  }

  async saveProjectData(projectId: string, blob: Blob, metadata: ProjectMetadata): Promise<boolean> {
    try {
      if (!(await this.ensureRestoredProjectUsers(metadata))) return false;
      const itemsSaved = await this.storageHandler.saveProjectItems2(projectId, blob);
      if (!itemsSaved) return false;
      return await this.storageHandler.saveProjectMetadata(projectId, metadata.toJSON());
    } catch (error) {
      console.error("Project blob or metadata could not be saved", projectId, error);
      return false;
    }
  }

  async loadProjectData(projectId: string): Promise<{ blob: Blob; metadata: ProjectMetadata } | undefined> {
    try {
      const [blob, metadataData] = await Promise.all([
        this.storageHandler.loadProjectItemsBlob(projectId),
        this.storageHandler.loadProjectMetadata(projectId),
      ]);
      if (!blob || !metadataData) return undefined;
      const metadata = new ProjectMetadata();
      metadata.fromJSON(metadataData);
      metadata.loadedFrom = this.storageHandler.isLocalStorgeHandle ? "local" : "server";
      return { blob, metadata };
    } catch (error) {
      console.error("Project blob or metadata could not be loaded", projectId, error);
      return undefined;
    }
  }

  async deleteProject(projectId: string): Promise<boolean> {
    let relatedProjectIds: string[] = [];
    try {
      const projects = await this.storageHandler.loadProjectList(state.loggedInUser.email);
      if (Array.isArray(projects)) {
        relatedProjectIds = projects
          .map((project) => String(project?.id ?? ""))
          .filter((id) => id.startsWith(`${projectId}-snapshot`));
      }
    } catch (error) {
      console.error("Project snapshots could not be listed before deletion", projectId, error);
    }

    const idsToDelete = [...new Set([...relatedProjectIds, projectId])];
    const storageResults = await Promise.all(
      idsToDelete.map(async (id) => {
        const [itemsDeleted, metadataDeleted] = await Promise.all([
          this.storageHandler.deleteProjectItems(id),
          this.storageHandler.deleteProjectMetadata(id),
        ]);
        return itemsDeleted && metadataDeleted;
      }),
    );

    // Offline copies, manual snapshots and every generation of autobackups
    // live in the local database even when the active handler is the server.
    const localSnapshotsDeleted = await this.localStorageHandler.deleteProjectSnapshots(projectId);
    const [localItemsDeleted, localMetadataDeleted] = await Promise.all([
      this.localStorageHandler.deleteProjectItems(projectId),
      this.localStorageHandler.deleteProjectMetadata(projectId),
    ]);

    return storageResults.every(Boolean) && localSnapshotsDeleted && localItemsDeleted && localMetadataDeleted;
  }

  async loadProjectList(user: string): Promise<Array<any>> {
    const res = await this.storageHandler.loadProjectList(user);

    if (!this.storageHandler.isLocalStorgeHandle) {
      //
      // if we load the list from the server
      // we need to add the "localCopied" field for all projects from the localStorage
      //
      for (const projectMetadataRaw of res) {
        const localMetadata = await this.localStorageHandler.loadProjectMetadata(projectMetadataRaw.id);
        if (localMetadata) {
          projectMetadataRaw.localCopied = localMetadata.localCopied;
          projectMetadataRaw.lastModifiedLocal = localMetadata.lastModifiedLocal;
        } else {
          projectMetadataRaw.localCopied = undefined;
        }
        projectMetadataRaw.loadedFrom = "server"; // no itx
      }
    } else {
      //
      // if we load the list from localStorage
      // the field "localCopied" is set anyway.
      //
      for (const projectMetadataRaw of res) {
        projectMetadataRaw.loadedFrom = "local"; // no itx
      }
    }

    return res;
  }

  async userIsLoggedIn(): Promise<boolean> {
    return await this.storageHandler.userIsLoggedIn();
  }

  private async handleLoIdent(email: string) {
    const i = setInterval(async () => {
      try {
        if ((window as any).LO) {
          console.log("luckyorange", (window as any).LO);
          const w = window as any;
          const LO: any = w.LO;
          if (LO) {
            clearInterval(i);
            try {
              // Track an event
              await LO.$internal.ready("events");

              LO.events.track("Login", { email: email }); // no itx
              state.events = LO.events; // .track("My Event");

              // Or, identify a visitor
              await LO.$internal.ready("visitor"); // no itx
              LO.visitor.identify(email, { email: email });
            } catch (ex) {}
          }
        }
      } catch (ex) {}
    }, 1000);
  }

  //
  // returns undefined if fails
  //
  async login(params: { email: string; password: string }): Promise<any | undefined> {
    // this.handleLoIdent(params.email);
    this.creditals = params;
    const res = await this.storageHandler.login(params);
    return res;
  }

  async loadPdfFile(id: string): Promise<Blob | undefined> {
    return this.storageHandler.loadPdfFile(id);
  }

  async savePdfFile(id: string, blob: Blob): Promise<boolean> {
    return this.storageHandler.savePdfFile(id, blob);
  }

  async saveInstallationChecks(projectId: string, ownerId: string, data: any, saveOnlyLocal: boolean): Promise<void> {
    await this.localStorageHandler.saveInstallationChecks(projectId, ownerId, data);

    if (!saveOnlyLocal && !this.storageHandler.isLocalStorgeHandle) {
      await this.storageHandler.saveInstallationChecks(projectId, ownerId, data);
    }
  }

  async loadInstallationChecksFromServer(projectId: string, ownerId: string): Promise<any | undefined> {
    if (!this.storageHandler.isLocalStorgeHandle) {
      return await this.storageHandler.loadInstallationChecks(projectId, ownerId);
    } else {
      return undefined;
    }
  }

  async loadInstallationChecksFromLocalStorage(projectId: string, ownerId: string): Promise<any | undefined> {
    return await this.localStorageHandler.loadInstallationChecks(projectId, ownerId);
  }

  async loadOnlineProjectMetadata(id: string): Promise<any | undefined> {
    try {
      const onlineProjectMetadata = new ProjectMetadata();
      // if we work offline
      const serverDataBaseHandler = new ServerDataBaseHandler();

      if (!state.isLoggedIn) {
        const res = await serverDataBaseHandler.login(universalstorageHandler.creditals);
        if (res?.token) {
          setSession(res.token);
          state.isLoggedIn = true;
        }
      }

      if (state.isLoggedIn) {
        const data = await serverDataBaseHandler.loadProjectMetadata(id);
        return data;
      }
    } catch (ex) {
      console.error(ex);
    }

    return undefined;
  }
}

const universalstorageHandler = new UniversalstorageHandler();
(globalThis as any).universalstorageHandler = universalstorageHandler;
export default universalstorageHandler;
